import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, IconButton,
  InputAdornment, Paper, Skeleton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { getTeams, deleteTeam, getOutcomesList, getApplicationsList } from './services/capacityApi';

export default function TeamsListView({
  onSelectTeam, onCreateTeam, isReadOnly,
  search, onSearchChange,
  filterOutcome, onFilterOutcomeChange,
  filterApplication, onFilterApplicationChange,
}) {
  const [teams, setTeams] = useState([]);
  const [outcomeOptions, setOutcomeOptions] = useState([]);
  const [applicationOptions, setApplicationOptions] = useState([]);
  const [outcomeMap, setOutcomeMap] = useState({});
  const [applicationMap, setApplicationMap] = useState({});
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    // Phase 1: load teams immediately (fast — local JSON)
    getTeams()
      .then((res) => setTeams(res?.teams || []))
      .catch(() => setError('Failed to load teams'))
      .finally(() => setTeamsLoading(false));

    // Phase 2: load filter options in background (may be slow — S3)
    Promise.all([getOutcomesList(), getApplicationsList()])
      .then(([oRes, aRes]) => {
        const outcomes = oRes?.outcomes || [];
        setOutcomeOptions(outcomes);
        const oMap = {};
        for (const o of outcomes) oMap[o.id] = o.name;
        setOutcomeMap(oMap);

        const apps = aRes?.applications || [];
        setApplicationOptions(apps);
        const aMap = {};
        for (const a of apps) aMap[a.id] = a.name;
        setApplicationMap(aMap);
      })
      .finally(() => setFiltersLoading(false));
  }, []);

  async function handleDelete(e, teamId) {
    e.stopPropagation();
    if (!window.confirm('Delete this team and all its sprints? This cannot be undone.')) return;
    setDeleting(teamId);
    try {
      await deleteTeam(teamId);
      setTeams((prev) => prev.filter((t) => t.id !== teamId));
    } catch {
      setError('Failed to delete team');
    } finally {
      setDeleting(null);
    }
  }

  const filtered = useMemo(
    () =>
      teams
        .filter((t) => !search || t.name.toLowerCase().includes(search.toLowerCase()))
        .filter((t) => !filterOutcome || (t.outcome_ids || []).includes(filterOutcome.id))
        .filter((t) => !filterApplication || (t.application_ids || []).includes(filterApplication.id)),
    [teams, search, filterOutcome, filterApplication]
  );

  const colSpan = isReadOnly ? 6 : 7;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Capacity Planner</Typography>
        <TextField
          size="small"
          placeholder="Search teams…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ width: 200 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')}><ClearIcon sx={{ fontSize: 14 }} /></IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Autocomplete
          size="small"
          options={outcomeOptions}
          getOptionLabel={(o) => o.name}
          value={filterOutcome}
          onChange={(_, v) => onFilterOutcomeChange(v)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          loading={filtersLoading}
          disabled={filtersLoading}
          sx={{ width: 200 }}
          renderInput={(params) => <TextField {...params} label="Filter by outcome" />}
        />
        <Autocomplete
          size="small"
          options={applicationOptions}
          getOptionLabel={(a) => a.name}
          value={filterApplication}
          onChange={(_, v) => onFilterApplicationChange(v)}
          isOptionEqualToValue={(a, v) => a.id === v.id}
          loading={filtersLoading}
          disabled={filtersLoading}
          sx={{ width: 200 }}
          renderInput={(params) => <TextField {...params} label="Filter by application" />}
        />
        {!isReadOnly && (
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={onCreateTeam}>
            New Team
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!teamsLoading && teams.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <GroupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">No teams yet.</Typography>
          {!isReadOnly && (
            <Button variant="outlined" sx={{ mt: 2 }} onClick={onCreateTeam} startIcon={<AddIcon />}>
              Create your first team
            </Button>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="center">Members</TableCell>
                <TableCell align="center">Sprints</TableCell>
                <TableCell>Outcomes</TableCell>
                <TableCell>Applications</TableCell>
                <TableCell>Description</TableCell>
                {!isReadOnly && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {teamsLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: colSpan }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <>
                  {filtered.map((team) => {
                    const outcomeIds = team.outcome_ids || [];
                    const appIds = team.application_ids || [];
                    return (
                      <TableRow
                        key={team.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => onSelectTeam(team.id)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{team.name}</Typography>
                        </TableCell>
                        <TableCell align="center">{team.member_count}</TableCell>
                        <TableCell align="center">{team.sprint_count}</TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {outcomeIds.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {outcomeIds.slice(0, 3).map((id) => (
                                <Chip
                                  key={id}
                                  label={outcomeMap[id] || id.substring(0, 8) + '…'}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  sx={{ fontSize: '0.6rem', height: 18 }}
                                />
                              ))}
                              {outcomeIds.length > 3 && (
                                <Chip label={`+${outcomeIds.length - 3}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {appIds.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {appIds.slice(0, 3).map((id) => (
                                <Chip
                                  key={id}
                                  label={applicationMap[id] || id.substring(0, 8) + '…'}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  sx={{ fontSize: '0.6rem', height: 18 }}
                                />
                              ))}
                              {appIds.length > 3 && (
                                <Chip label={`+${appIds.length - 3}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>
                          {team.description ? (
                            <Tooltip title={team.description.length > 50 ? team.description : ''}>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 220 }}>
                                {team.description.length > 50
                                  ? `${team.description.slice(0, 50)}…`
                                  : team.description}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        {!isReadOnly && (
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="Delete team">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={deleting === team.id}
                                  onClick={(e) => handleDelete(e, team.id)}
                                >
                                  {deleting === team.id
                                    ? <CircularProgress size={14} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center">
                        <Typography variant="caption" color="text.secondary">
                          No teams match the current filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
